import { send } from '../../../../../../src/network';
import {
  type RatablePairing,
  type PaginatedRatablePairingListList,
  type PaginatedPairingListListWithSideloadedTags,
} from '../../../../../../src/types/PaginatedRatablePairingListList';
import { type Tag } from '../../../../../../src/types/Tag';

export { type PaginatedRatablePairingListList };

export type Params = {
  bypass_viewed?: boolean;
  feedback_modal_eligible?: boolean;
  include_previously_rated_pairings?: boolean;
  limit?: string;
  offset?: string;
  customerId: number;
  recent_order_count?: number;
  recent_order_date?: string;
};

type Return = {
  status: number;
  data: PaginatedRatablePairingListList;
};

export async function getApiV2CustomersCustomerIdRatablePairings(
  params: Params
): Promise<Return> {
  const queryParams = new URLSearchParams();

  if (params.bypass_viewed !== undefined) {
    queryParams.append('bypass_viewed', params.bypass_viewed.toString());
  }

  if (params.feedback_modal_eligible !== undefined) {
    queryParams.append(
      'feedback_modal_eligible',
      params.feedback_modal_eligible.toString()
    );
  }

  if (params.include_previously_rated_pairings !== undefined) {
    queryParams.append(
      'include_previously_rated_pairings',
      params.include_previously_rated_pairings.toString()
    );
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit);
  }

  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset);
  }

  if (params.recent_order_count !== undefined) {
    queryParams.append(
      'recent_order_count',
      params.recent_order_count.toString()
    );
  }

  if (params.recent_order_date !== undefined) {
    queryParams.append('recent_order_date', params.recent_order_date);
  }

  queryParams.append('sideload', 'tags');

  const resp = await send<PaginatedPairingListListWithSideloadedTags>({
    path: `/api/v2/customers/${params.customerId}/ratable_pairings/`,
    method: 'GET',
    query: queryParams,
  });

  return transformSideloadedResponse(resp);
}

function transformSideloadedResponse(resp: {
  status: number;
  data: PaginatedPairingListListWithSideloadedTags;
}) {
  const { data } = resp;
  const { results } = data;
  const { pairings, tags } = results;

  const tagsMap = new Map();

  for (const tag of tags) {
    tagsMap.set(tag.id, tag);
  }
  const transformedResults: RatablePairing[] = [];

  for (const pairing of pairings) {
    transformedResults.push({
      ...pairing,

      tags: pairing.tags
        .map((tagId) => tagsMap.get(tagId))
        .filter(Boolean) as Tag[],

      ingredients: pairing.ingredients.map((ingredient) => {
        return {
          ...ingredient,
          tags: ingredient.tags
            .map((tagId) => tagsMap.get(tagId))
            .filter(Boolean) as Tag[],
        };
      }),
    });
  }

  return {
    ...resp,
    data: {
      ...data,
      results: transformedResults,
    },
  };
}
